/** Application-observed termination of an exact native turn. This is a
 * ReadOS receipt, never a replacement or forgery of an upstream turn event. */
export type ReadOSTurnTerminalEvent = {
  method:'reados/turnTerminal';
  params:{
    ownerThreadId:string;
    threadId:string;
    turnId:string;
    status:'interrupted'|'failed';
    /** Observation time. For legacy/restart receipts this does not establish
     * when execution ended and must not be presented as processing duration. */
    completedAtMilliseconds:number;
    reason:'runtime-retired'|'process-restart'|'legacy-interruption'|'sandbox-oom';
  };
};

export function isReadOSTurnTerminalEvent(value:unknown):value is ReadOSTurnTerminalEvent {
  if(!value||typeof value!=='object'||Array.isArray(value))return false;
  const event=value as Record<string,unknown>,p=event.params;
  if(event.method!=='reados/turnTerminal'||!p||typeof p!=='object'||Array.isArray(p))return false;
  const params=p as Record<string,unknown>;
  return ['ownerThreadId','threadId','turnId'].every(key=>typeof params[key]==='string'&&(params[key] as string).length>0)
    && ['interrupted','failed'].includes(params.status as string)
    && typeof params.completedAtMilliseconds==='number'&&Number.isFinite(params.completedAtMilliseconds)&&params.completedAtMilliseconds>0
    && ['runtime-retired','process-restart','legacy-interruption','sandbox-oom'].includes(params.reason as string);
}

/** Unchanged upstream lifecycle JSON. The authenticated Web owner scopes its
 * delivery separately; inherited turns retain their original native identity. */
export type NativeTurnLifecycleEvent={
  method:'turn/started'|'turn/completed'|'turn/interrupted'|'turn/failed';
  params:Record<string,unknown>&{threadId:string};
};

export function nativeTurnLifecycleIdentity(event:NativeTurnLifecycleEvent):{threadId:string;turnId:string} {
  const turn=event.params.turn;
  const id=turn&&typeof turn==='object'&&!Array.isArray(turn)?(turn as Record<string,unknown>).id:undefined;
  return {threadId:event.params.threadId,turnId:(typeof id==='string'&&id.length>0?id:event.params.turnId) as string};
}

export function isNativeTurnLifecycleEvent(value:unknown):value is NativeTurnLifecycleEvent {
  if(!value||typeof value!=='object'||Array.isArray(value))return false;
  const event=value as Record<string,unknown>,p=event.params;
  if(!['turn/started','turn/completed','turn/interrupted','turn/failed'].includes(event.method as string)||!p||typeof p!=='object'||Array.isArray(p))return false;
  const params=p as Record<string,unknown>,turn=params.turn;
  const id=turn&&typeof turn==='object'&&!Array.isArray(turn)?(turn as Record<string,unknown>).id:undefined;
  return typeof params.threadId==='string'&&params.threadId.length>0
    &&(typeof id==='string'&&id.length>0||typeof params.turnId==='string'&&params.turnId.length>0);
}
